import React, { useState } from 'react';
import MuscleIllustration from './MuscleIllustration';
import { photoForExercise } from '../data/exercisePhotos';

/**
 * ExerciseVisual — renders a real open-source photo of the exercise when
 * we have a curated URL for its name, and falls back to the stylised
 * MuscleIllustration SVG for custom / unmapped exercises.
 *
 * The photo layer uses eager loading (session cards are expanded on-tap so
 * we're already visible) and a `loaded` state so the SVG stays put until
 * the JPG has actually decoded — no ugly flash of missing image.
 */
export default function ExerciseVisual({ exercise, size = 64, className = '', testId }) {
  const url = photoForExercise(exercise?.name);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const useSvg = !url || failed;

  if (useSvg) {
    return (
      <MuscleIllustration
        muscleGroup={exercise?.muscle_group === 'Cardio' ? 'Cardio' : exercise?.muscle_group}
        size={size}
        className={className}
        testId={testId || `illustration-${exercise?.id || 'x'}`}
      />
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-ios bg-graphite/5 flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
      data-testid={testId || `photo-${exercise?.id}`}
    >
      {/* SVG placeholder while the photo decodes */}
      {!loaded && (
        <MuscleIllustration
          muscleGroup={exercise?.muscle_group === 'Cardio' ? 'Cardio' : exercise?.muscle_group}
          size={size}
        />
      )}
      <img
        src={url}
        alt={exercise?.name || 'Exercise'}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}
